import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid2";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  DialogTitle,
  IconButton,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FormInputText } from "../../../components/common/FormInputText";
import { addressSchema } from "../../../utilities/schema";
import { FormInputTextArea } from "../../../components/common/FormTextArea";
import { useAuth } from "../../../hooks/AuthContext";
import { Info } from "@phosphor-icons/react";
import GoogleMapContainer from "../../pre-checkout/GoogleMapContainer";
import { useRef, memo } from "react";
import useApiMutation from "../../../hooks/useApiMutation";
import { toast } from "react-toastify";
import {
  editFullAddress,
  getAddressDetails,
  listAllAddress,
  storeFullAddress,
} from "../../../utilities/service";
import { useEffect } from "react";
import { useCallback } from "react";
import useApiQuery from "../../../hooks/useApiQuery";
import AddressFormSkeleton from "../../my-address/AddressFormSkeleton";

function AddressDialog(props) {
  const {
    addressCreateFrom,
    setSelectAddressDrawer,
    setNewlyCreatedAddressId,
  } = useAuth();
  const [warningshown, setWarningShown] = useState(false);

  const { open, setOpen, addressListFetch, selectedAddressId = null } = props;
  const { user } = useAuth();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data: addressData,
    error,
    isLoading,
    refetch,
  } = useApiQuery({
    queryKey: ["getAddressDetails", selectedAddressId],
    queryFn: () => getAddressDetails(selectedAddressId),
    enabled: selectedAddressId ? true : false,
  });

  const methods = useForm({
    resolver: yupResolver(addressSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      mobile: "",
      full_address: "",
      pincode: "",
      address_type: "Home",
    },
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors, isValid },
  } = methods;

  const [loading, setLoading] = useState(false);
  const [maxWidth, setMaxWidth] = useState("md");
  const [enteredPincode, setEnteredPincode] = useState(null);

  const addressDetailsRef = useRef(null);
  const mapRef = useRef(null);
  const isFormInitialized = useRef(false);
  const skipDebounce = useRef(false);
  const isMapSelection = useRef(false);
  const isPasteRef = useRef(false); // ✅ FIX: tracks whether last change was a paste

  const addressType = watch("address_type");
  const fullAddress = watch("full_address");
  const [isPincodeEnabled, setIsPincodeEnabled] = useState(false);

  // ✅ SINGLE debounce useEffect (old duplicate removed — that was the root cause).
  // - Skips map search on paste (isPasteRef) so pasted address is never overwritten
  // - Skips when skipDebounce is set (after map selection updates the field)
  // - Only fires after 10+ chars so typing 2-3 letters doesn't hijack the field
  // - 1200ms delay gives user time to finish typing before map search triggers
  // useEffect(() => {
  //   if (skipDebounce.current) {
  //     skipDebounce.current = false;
  //     return;
  //   }

  //   if (isPasteRef.current) {
  //     isPasteRef.current = false;
  //     return;
  //   }

  //   if (fullAddress && fullAddress.length > 10) {
  //     isMapSelection.current = false;
  //     const timer = setTimeout(() => {
  //       if (mapRef.current) {
  //         mapRef.current.searchFromInput(fullAddress);
  //       }
  //     }, 1200);

  //     return () => clearTimeout(timer);
  //   }
  // }, [fullAddress]);

  const { mutate } = useApiMutation({
    mutationFn: (param) =>
      selectedAddressId ? editFullAddress(param) : storeFullAddress(param),
    options: {
      onSuccess: (data) => {
        if (!data?.error) {
          console.log(data?.data?.[0]);
          toast.success(data?.message);
          addressListFetch();
          handleClose();
          if (addressCreateFrom == "CART_SIDE_NAV") {
            setSelectAddressDrawer(true);
            setNewlyCreatedAddressId(data?.data?.[0]);
          }
        } else {
          toast.error(data?.message);
        }
      },
      onError: (error) => {
        console.log(error);
        toast.error(error?.message);
      },
    },
  });

  const handleClose = () => {
    setOpen(false);
    reset();
    isFormInitialized.current = false;
  };

  const defaultCoordinates = React.useMemo(
    () => ({
      lat: parseFloat(addressData?.latitude) || 0,
      lng: parseFloat(addressData?.longitude) || 0,
    }),
    [addressData?.latitude, addressData?.longitude],
  );

  // Callback to update Full Address from map
  const handleAddressFromMap = useCallback(
    (address) => {
      skipDebounce.current = true;

      // Only update full_address if this is an explicit map selection (click/drag)
      // NOT from typing/debounce search
      // if (isMapSelection.current) {
      //   setValue("full_address", address, {
      //     shouldValidate: true,
      //     shouldDirty: true,
      //   });
      // }

      // Enable & set pincode from map
      setTimeout(() => {
        const mapPincode = addressDetailsRef.current?.pincode;
        console.log("Map pincode:", mapPincode);
        console.log("addressDetailsRef:", addressDetailsRef.current);

        if (mapPincode) {
          setIsPincodeEnabled(true);
          setValue("pincode", mapPincode, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }, 500);
    },
    [setValue],
  );

  // Wrapper to set flag when map selection happens (click/drag/search select)
  const handleMapSelectionWithFlag = useCallback(
    (address) => {
      isMapSelection.current = true;
      handleAddressFromMap(address);
    },
    [handleAddressFromMap],
  );

  // const storeOrEditAddress = async (data) => {
  //   if (data?.pincode && !/^\d{6}$/.test(data.pincode)) {
  //     toast.warning("Invalid Pincode");
  //     return;
  //   }
  //   if (!warningshown && data?.pincode !== addressDetailsRef.current?.pincode) {
  //     setEnteredPincode(data?.pincode);
  //     setWarningShown(true);
  //     toast.warning("Pincode is not matching with the selected location");
  //     return;
  //   }
  //   setWarningShown(false);
  //   setLoading(true);
  //   const param = {
  //     customer_id: user?.customer_id,
  //     billing_name: data?.name,
  //     billing_mobile: data?.mobile,
  //     address_type: data?.address_type || "Home",
  //     default_address: 0,
  //     latitude: addressDetailsRef.current.coordinates?.lat,
  //     longitude: addressDetailsRef.current.coordinates?.lng,
  //     full_address: data?.full_address,
  //     area: addressDetailsRef.current?.area,
  //     address_city_district: addressDetailsRef.current?.district,
  //     address_state: addressDetailsRef.current?.state,
  //     address_pincode: addressDetailsRef.current?.pincode,
  //     region_id: user?.region_id,
  //   };
  const storeOrEditAddress = async (data) => {
    if (data?.pincode && !/^\d{6}$/.test(data.pincode)) {
      toast.warning("Invalid Pincode");
      return;
    }
    if (!warningshown && data?.pincode !== addressDetailsRef.current?.pincode) {
      setEnteredPincode(data?.pincode);
      setWarningShown(true);
      toast.warning("Pincode is not matching with the selected location");
      return;
    }
    setWarningShown(false);
    setLoading(true);

    // ✅ FIX: Use addressDetailsRef for full_address when map was used,
    // fall back to form field value if map was never interacted with
    const resolvedFullAddress =
      addressDetailsRef.current?.formattedAddress || // if your ref stores this
      data?.full_address;

    const param = {
      customer_id: user?.customer_id,
      billing_name: data?.name,
      billing_mobile: data?.mobile,
      address_type: data?.address_type || "Home",
      default_address: 0,
      latitude: addressDetailsRef.current?.coordinates?.lat,
      longitude: addressDetailsRef.current?.coordinates?.lng,
      full_address: resolvedFullAddress, // ✅ consistent with lat/lng
      area: addressDetailsRef.current?.area,
      address_city_district: addressDetailsRef.current?.district,
      address_state: addressDetailsRef.current?.state,
      address_pincode: addressDetailsRef.current?.pincode,
      region_id: user?.region_id,
    };
    if (selectedAddressId) {
      param.customer_address_id = selectedAddressId;
      mutate(param);
    } else {
      mutate(param);
    }
    setLoading(false);
  };

  // Initialize form only once when dialog opens
  useEffect(() => {
    if (open && !isLoading && !isFormInitialized.current) {
      if (selectedAddressId && addressData) {
        const rawMobile = String(
          addressData?.billing_mobile || user?.customer_mobile || "",
        );
        const formattedMobile = rawMobile.startsWith("+")
          ? rawMobile
          : `+${rawMobile}`;

        reset({
          name: addressData?.billing_name || user?.customer_name,
          mobile: formattedMobile,
          full_address: addressData?.full_address || "",
          pincode: addressData?.address_pincode || "",
          address_type: addressData?.address_type || "Home",
        });
        setIsPincodeEnabled(true);
        isFormInitialized.current = true;
      } else if (!selectedAddressId) {
        const rawMobile = String(user?.customer_mobile || "");
        const formattedMobile = rawMobile.startsWith("+")
          ? rawMobile
          : `+${rawMobile}`;

        reset({
          name: user?.customer_name || "",
          mobile: formattedMobile,
          full_address: "",
          pincode: "",
          address_type: "Home",
        });
        isFormInitialized.current = true;
      }
    }

    if (!open) {
      isFormInitialized.current = false;
    }
  }, [open, selectedAddressId, addressData, isLoading]);

  return (
    <React.Fragment>
      <Dialog
        fullScreen={fullScreen}
        maxWidth={maxWidth}
        open={open}
        onClose={handleClose}
      >
        <DialogTitle
          sx={{
            m: 0,
            pt: 2,
            px: 5,
            bgcolor: "var(--neutral-white)",
            fontSize: "20px",
            fontWeight: 600,
          }}
        >
          Address
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "var(--neutral-white)" }}>
          {isLoading ? (
            <AddressFormSkeleton />
          ) : (
            <Grid container spacing={3} sx={{ p: { xs: 1, sm: 2 } }}>
              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Box>
                  <Grid container spacing={2} sx={{ height: "100%" }}>
                    <Grid size={6}>
                      <FormInputText
                        name="name"
                        control={control}
                        label="Contact Name"
                        errors={errors}
                        type="text"
                        autofocus={true}
                      />
                    </Grid>
                    <Grid size={6}>
                      <FormInputText
                        name="mobile"
                        control={control}
                        label="Contact Number"
                        errors={errors}
                        type="text"
                        autofocus={true}
                         maxLength={13}
                      />
                    </Grid>

                    <Grid size={12}>
                      <FormInputTextArea
                        name="full_address"
                        control={control}
                        label="Full Address"
                        errors={errors}
                        type="text"
                        autofocus={true}
                        onPaste={() => {
                          isPasteRef.current = true;
                        }}
                      />
                    </Grid>
                    <Grid size={12}>
                      <FormInputText
                        name="pincode"
                        control={control}
                        label="Pincode"
                        errors={errors}
                        type="text"
                        maxLength={6}
                        disabled={!isPincodeEnabled}
                        onFocus={() => setIsPincodeEnabled(true)}
                      />
                    </Grid>
                    <Grid size={12}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 500,
                          fontSize: "14px",
                          lineHeight: "157%",
                          color: "var(--neutral-800)",
                          textAlign: "left",
                          pb: 1,
                        }}
                      >
                        Address Type
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <AddressTypeButton
                          variant="outlined"
                          selected={addressType === "Home"}
                          onClick={() => setValue("address_type", "Home")}
                        >
                          Home
                        </AddressTypeButton>
                        <AddressTypeButton
                          variant="outlined"
                          selected={addressType === "Work"}
                          onClick={() => setValue("address_type", "Work")}
                        >
                          Work
                        </AddressTypeButton>
                        <AddressTypeButton
                          variant="outlined"
                          selected={addressType === "Others"}
                          onClick={() => setValue("address_type", "Others")}
                        >
                          Others
                        </AddressTypeButton>
                      </Stack>
                    </Grid>
                    <Grid size={12}>
                      <Typography
                        variant="h3"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          fontWeight: 500,
                          fontSize: "12px",
                          lineHeight: "133%",
                          textAlign: "center",
                          color: "var(--neutral-800)",
                          mt: 2,
                        }}
                      >
                        <Info size={18} color="#0000FF" />
                        This google map only for efficient deliver experience
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  mt: { xs: 2, md: 0 },
                }}
              >
                <GoogleMapContainer
                  ref={mapRef}
                  addressDetailsRef={addressDetailsRef}
                  defaultCoordinates={defaultCoordinates}
                  onAddressSelect={handleMapSelectionWithFlag}
                />

                {warningshown && (
                  <Alert severity="warning" sx={{ mt: 2, fontSize: "12px" }}>
                    Pincode mismatch: Map shows{" "}
                    <b>{addressDetailsRef.current?.pincode}</b>, you entered{" "}
                    <b>{enteredPincode}</b>. Are you sure to save?
                  </Alert>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "center",
                    mt: 2,
                    gap: 2,
                  }}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleClose}
                    size="small"
                    sx={{ py: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    disabled={!isValid || loading}
                    onClick={handleSubmit(storeOrEditAddress)}
                    sx={{ py: 1 }}
                  >
                    {loading ? (
                      <CircularProgress color="white" size="21px" />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}

const AddressTypeButton = styled(Button)(({ theme, selected }) => ({
  borderRadius: "30px",
  padding: "10px",
  width: "80px",
  height: "32px",
  borderColor: selected ? theme.palette.primary.main : "var(--neutral-300)",
  backgroundColor: selected ? "var(--primary-red-100)" : "transparent",
  color: selected ? theme.palette.primary.main : "var(--neutral-800)",
}));

export default React.memo(AddressDialog);
